import React, {
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Callout, Intent } from '@blueprintjs/core';
import cloneDeep from 'lodash.clonedeep';
import * as actions from 'data/accessFunction/accessFunction.actions';
import * as accessFunctionSelectors from 'data/accessFunction/accessFunction.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as targetTemplateSelectors from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.selectors';
import { RootState } from 'data/root.reducers';
import { getSelectedDeliveryFunctionId } from 'global/global-selectors';
import {
  EricssonNETargetInfoFormData,
  EricssonNETargetInfoMonitoringFormData,
  TargetModelAccessFunction,
  TargetModelAfCCCSettings,
} from 'data/target/target.types';
import Form, { FieldUpdateHandlerParam, ChangedField } from 'shared/form';
import { FormTemplate, FormTemplateSection, FormTemplateValidator } from 'data/template/template.types';
import { AdditionalMO } from 'data/accessFunction/accessFunction.types';
import { DEPLOYMENT_TUNISIA } from 'xcipio/constants';
import { getTemplateOptions } from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.adapter';
import { TargetDeliveryTemplateAccessFunction } from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.types';
import { ericssonNETargetInfoTemplate } from './ericssonNETargetInfoTemplate';
import { ericssonNETargetInfoTemplateFIX } from './ericssonNETargetInfoTemplateFIX';
import { ericssonNETargetInfoTemplateHLR } from './ericssonNETargetInfoTemplateHLR';
import { ericssonNETargetInfoTemplateMSC } from './ericssonNETargetInfoTemplateMSC';
import { ericssonNETargetInfoTemplateSIPSERVER } from './ericssonNETargetInfoTemplateSIPSERVER';
import { ericssonNETargetInfoTemplateOVSIPSERVER } from './ericssonNETargetInfoTemplateOVSIPSERVER';
import { ericssonNETargetInfoTemplateOVMOWLAN } from './ericssonNETargetInfoTemplateOVMOWLAN';
import { ericssonNETargetInfoTemplateOVGPRS } from './ericssonNETargetInfoTemplateOVGPRS';
import EricssonNETargetInfoTabs, { EricssonNETargetInfoTabsRef } from './EricssonNETargetInfoTabs';
import './ericsson-ne-target-info.scss';
import { createEricssonNETargetInfoBasedOnTemplate } from './ericssonNETargetInfoTemplate.factory';
import {
  adaptTabData,
  adaptTemplateVisibilityMode,
  adaptToForm,
  adaptToSubmit,
  isEricssonNEAccessFunctionSupported,
  getTargetTemplateValues,
  adaptTemplate,
  handleTargetDeliveryTemplateUpdate,
  adaptDataBasedOnDeployment
} from './utils';

export const getTargetTemplateByNeType = (erkneType: string) => {
  let targetInfoTemplate = null;

  if (erkneType === 'GPRS') {
    targetInfoTemplate = ericssonNETargetInfoTemplate;
  }

  if (erkneType === 'HLR') {
    targetInfoTemplate = ericssonNETargetInfoTemplateHLR;
  }

  if (erkneType === 'MSC') {
    targetInfoTemplate = ericssonNETargetInfoTemplateMSC;
  }

  if (erkneType === 'FIX') {
    targetInfoTemplate = ericssonNETargetInfoTemplateFIX;
  }

  if (erkneType === 'SIPSERVER') {
    targetInfoTemplate = ericssonNETargetInfoTemplateSIPSERVER;
  }

  if (erkneType === 'OV_SIPSERVER') {
    targetInfoTemplate = ericssonNETargetInfoTemplateOVSIPSERVER;
  }

  if (erkneType === 'OV_MOWLAN') {
    targetInfoTemplate = ericssonNETargetInfoTemplateOVMOWLAN;
  }

  if (erkneType === 'OV_GPRS') {
    targetInfoTemplate = ericssonNETargetInfoTemplateOVGPRS;
  }

  return targetInfoTemplate;
};

const mapState = (state: RootState) => ({
  deliveryFunctionId: getSelectedDeliveryFunctionId(state),
  accessFunction: accessFunctionSelectors.getAccessFunction(state),
  deployment: discoverySelectors.getDiscoveryDeployment(state),
  targetDeliveryTemplates: targetTemplateSelectors.getTargetDeliveryTemplateAccessFunctionList(state),
});

const mapDispatch = {
  getAccessFunctionWithViewOptions: actions.getAccessFunctionWithViewOptions,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

export type Props = PropsFromRedux & {
  targetAccessFunction: TargetModelAccessFunction
  accessFunctionSettings: TargetModelAfCCCSettings[],
  selectedTargetType: string,
  isEditing: boolean,
  i18nKeyPrefix?: string,
  interceptType?: string
  onUpdate: (afSettings: TargetModelAfCCCSettings[]) => void
};

export const ERICSSON_NE_TARGET_INFO_I18N_KEY = 'warrant.target.form.ericssonNE';

const EricssonNETargetInfo: FunctionComponent<Props> = ({
  interceptType,
  targetAccessFunction,
  accessFunctionSettings,
  i18nKeyPrefix = ERICSSON_NE_TARGET_INFO_I18N_KEY,
  deliveryFunctionId,
  accessFunction,
  selectedTargetType,
  isEditing,
  targetDeliveryTemplates,
  onUpdate,
  getAccessFunctionWithViewOptions,
  deployment
}: Props) => {
  const [template, setTemplate] = useState<FormTemplate>();
  const [data, setData] = useState<Partial<EricssonNETargetInfoFormData>>({});
  const [type, setType] = useState<string | undefined>(interceptType);
  const [netype, setNetype] = useState<string | undefined>();
  const targetRef = useRef<Form>(null);
  const generalRef = useRef<Form>(null);
  const [isSupported, setIsSupported] = useState(true);
  const gprsTargetRef = useRef<Form>(null);
  const mscTargetRef = useRef<Form>(null);
  const fixTargetRef = useRef<Form>(null);
  const mcTargetRef = useRef<Form>(null);
  const tabsRef = useRef<EricssonNETargetInfoTabsRef>(null);
  const { t } = useTranslation();

  const targetAccessFunctionId = targetAccessFunction.id;

  useEffect(() => {
    if (targetAccessFunctionId) {
      setTemplate(undefined);
      setData({});
      setIsSupported(true);
      getAccessFunctionWithViewOptions(deliveryFunctionId, targetAccessFunctionId);
    }
  }, [targetAccessFunctionId, deliveryFunctionId, getAccessFunctionWithViewOptions]);

  useEffect(() => {
    setType(interceptType);
  }, [type, interceptType]);

  useEffect(() => {
    if (accessFunction != null && accessFunction.id === targetAccessFunctionId) {
      if (!isEricssonNEAccessFunctionSupported(accessFunction, selectedTargetType)) {
        setIsSupported(false);
        return;
      }

      const erkneType = (accessFunction?.additionalMOs as AdditionalMO[])[0]?.options.filter((option) => option.key === 'netype')[0].value;
      setNetype((accessFunction?.additionalMOs as AdditionalMO[])[0]?.options.filter((option) => option.key === 'netype')[0].value as string);

      setIsSupported(true);

      const targetInfoTemplate = getTargetTemplateByNeType(erkneType as string);
      let initialTemplate = adaptTemplateVisibilityMode(targetInfoTemplate as FormTemplate, isEditing);
      initialTemplate = adaptTemplate(targetInfoTemplate as FormTemplate, deployment, targetDeliveryTemplates, targetAccessFunction);
      if ((erkneType === 'OV_SIPSERVER' || erkneType === 'OV_MOWLAN' || erkneType === 'OV_GPRS') && type === 'CD' && initialTemplate) {
        (initialTemplate.EIMSMC as FormTemplateSection).fields.CC_MCID.hide = 'true';
        (initialTemplate.EIMSMC as FormTemplateSection).fields.CC_SUPPNEID.hide = 'true';
      } else if ((erkneType === 'OV_SIPSERVER' || erkneType === 'OV_MOWLAN' || erkneType === 'OV_GPRS') && initialTemplate) {
        (initialTemplate.EIMSMC as FormTemplateSection).fields.CC_MCID.hide = 'false';
        (initialTemplate.EIMSMC as FormTemplateSection).fields.CC_SUPPNEID.hide = 'false';
      }
      setTemplate(initialTemplate);

      const initialData = createEricssonNETargetInfoBasedOnTemplate(
        interceptType,
        erkneType as string,
        initialTemplate,
      );

      let adaptedData = adaptToForm(
        initialData,
        accessFunctionSettings,
        interceptType as string,
        erkneType as string,
      );

      adaptedData = adaptDataBasedOnDeployment(adaptedData, deployment, initialTemplate, targetDeliveryTemplates);

      setData(adaptedData);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessFunction, type, targetDeliveryTemplates]);

  useEffect(() => {
    if (targetAccessFunctionId == null || data.EIMSTARGET == null || !isEditing) {
      return;
    }

    const isTargetValid = targetRef.current?.isValid(true, false) ?? false;
    const isGprsTargetValid = gprsTargetRef.current?.isValid(true, false) ?? false;
    const isMSCtargetValid = mscTargetRef.current?.isValid(true, true) ?? false;
    const isFixTargetValid = fixTargetRef.current?.isValid(true, false) ?? false;
    const isMcTargetValid = mcTargetRef.current?.isValid(true, true) ?? false;
    const isEveryTabValid = tabsRef.current?.isValid(true, false) ?? false;
    let isValid = false;

    if (type === 'CD') {
      if (netype === 'GPRS') {
        isValid = isTargetValid && isGprsTargetValid;
      }

      if (netype === 'MSC') {
        isValid = isTargetValid && isMSCtargetValid;
      }

      if (netype === 'FIX') {
        isValid = isTargetValid && isFixTargetValid;
      }

      if (netype === 'SIPSERVER') {
        isValid = isTargetValid;
      }
    } else {
      if (netype === 'GPRS') {
        isValid = isTargetValid && isGprsTargetValid && isEveryTabValid;
      }

      if (netype === 'MSC') {
        isValid = isTargetValid && isMSCtargetValid && isEveryTabValid;
      }

      if (netype === 'FIX') {
        isValid = isTargetValid && isFixTargetValid && isEveryTabValid;
      }

      if (netype === 'SIPSERVER') {
        isValid = isTargetValid && isEveryTabValid;
      }
    }

    if (netype === 'HLR') {
      isValid = isTargetValid;
    }

    if (netype === 'OV_MOWLAN' || netype === 'OV_SIPSERVER' || netype === 'OV_GPRS') {
      isValid = isTargetValid && isMcTargetValid
    }

    const afSettings = adaptToSubmit(targetAccessFunctionId, data, isValid, type);

    onUpdate(afSettings);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, type]);

  const handleTabAdd = () => {
    const clonedData = cloneDeep(data);

    const newData = createEricssonNETargetInfoBasedOnTemplate(type, netype, template);
    const newTabData = (newData.EIMSMCNB as EricssonNETargetInfoMonitoringFormData[])[0];
    adaptTabData(clonedData, newTabData);
    (clonedData.EIMSMCNB as EricssonNETargetInfoMonitoringFormData[]).push(newTabData);

    setData(clonedData);
  };

  const handleTabRemove = (tabId: string | number) => {
    const clonedData = cloneDeep(data);

    clonedData.EIMSMCNB = clonedData.EIMSMCNB?.filter(({ temporaryId }) => temporaryId !== tabId);

    setData(clonedData);
  };

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam, formSection: 'EIMSTARGET' | 'EIMSGPRSTRGT' | 'EIMSMCNB' | 'EIMSMSCTRGT' | 'EIMSWRLNTRGT' | 'EIMSMC' | 'general', tabId?: string) => {
    let clonedData = cloneDeep(data);
    const clonedTemplate = cloneDeep(template as FormTemplate);

    if (formSection === 'general') {
      if (fieldName === 'targetDeliveryTemplate') {
        clonedData = handleTargetDeliveryTemplateUpdate(clonedData, targetDeliveryTemplates, value);
        clonedTemplate.formId = Date.now().toString();
      }
    }
    if (formSection === 'EIMSTARGET' || formSection === 'EIMSGPRSTRGT' || formSection === 'EIMSMSCTRGT' || formSection === 'EIMSWRLNTRGT' || formSection === 'EIMSMC') {
      clonedData = {
        ...clonedData,
        [formSection]: {
          ...clonedData[formSection],
          [fieldName]: value,
        },
      };

    }
    if (formSection === 'EIMSMCNB') {
      clonedData[formSection]?.map((tabFormData) => {
        if (tabFormData.temporaryId === tabId) {
          tabFormData[fieldName as keyof EricssonNETargetInfoMonitoringFormData] = value as string;
        }
      });
    }

    setTemplate(clonedTemplate);
    setData(clonedData);
  };

  if (!isSupported) {
    return (
      <Callout intent={Intent.WARNING}>
        {t('warrant.target.form.ericssonNE.notSupported')}
      </Callout>
    );
  }

  if (template == null) {
    return null;
  }

  let gprsTargetInfoTemplateSection = null;
  let mscTargetInfoTemplateSection = null;
  let monitoringTargetInfoTemplateSection = null;
  let fixTargetInfoTemplateSection = null;
  let mcTargetInfoTemplateSection = null

  const targetInfoTemplateSection = template.EIMSTARGET as FormTemplateSection;
  if (netype === 'GPRS') {
    gprsTargetInfoTemplateSection = template.EIMSGPRSTRGT as FormTemplateSection;
  }

  if (netype === 'MSC') {
    mscTargetInfoTemplateSection = template.EIMSMSCTRGT as FormTemplateSection;
  }

  if (netype === 'FIX') {
    fixTargetInfoTemplateSection = template.EIMSWRLNTRGT as FormTemplateSection;
  }

  if (netype === 'OV_SIPSERVER' || netype === 'OV_MOWLAN' || netype === 'OV_GPRS') {
    mcTargetInfoTemplateSection = template.EIMSMC as FormTemplateSection
  }

  monitoringTargetInfoTemplateSection = template.EIMSMCNB as FormTemplateSection;

  const shortIntValidator = (
    _propFields?: unknown,
    _stateFields?: unknown,
    value?: string,
    _lastSavedValue?: string,
    validator?: FormTemplateValidator,
  ) => {
    const values = value ?? '0';
    const intValue = parseInt(values, 10);
    const valid = (intValue >= 0 && intValue <= 65535);
    let message = '';
    if (!valid && validator?.customValidationFunction) {
      message = t('common.form.fieldError.shortIntValidator') ?? '';
    }

    return { isValid: valid, errorMessage: message };
  };

  const customValidationFunctionMap = {
    shortIntValidator
  }

  const onTargetFieldUpdate = (param: FieldUpdateHandlerParam) => handleFieldUpdate(param, 'EIMSTARGET');
  const onMSCTargetFieldUpdate = (param: FieldUpdateHandlerParam) => handleFieldUpdate(param, 'EIMSMSCTRGT');
  const onGPRSTargetFieldUpdate = (param: FieldUpdateHandlerParam) => handleFieldUpdate(param, 'EIMSGPRSTRGT');
  const onMonitoringFieldUpdate = (param: FieldUpdateHandlerParam, tabId?: string) => handleFieldUpdate(param, 'EIMSMCNB', tabId);
  const onFIXTargetFieldUpdate = (param: FieldUpdateHandlerParam) => handleFieldUpdate(param, 'EIMSWRLNTRGT');
  const onMcTargetFieldUpdate = (param: FieldUpdateHandlerParam) => handleFieldUpdate(param, 'EIMSMC');
  const onGeneralFieldUpdate = (param: FieldUpdateHandlerParam) => handleFieldUpdate(param, 'general');
  return (
    <div className="ericsson-ne-target-info" data-testid="EricssonNETargetInfo">
      <div className="header">
        {t('warrant.target.form.ericssonNE.targetInformation')}
      </div>
      <Form
        name={template.key}
        defaults={data.general}
        fields={(template.general as FormTemplateSection).fields}
        layout={(template.general as FormTemplateSection).metadata?.layout?.rows}
        ref={generalRef}
        flexFlow="row"
        fieldUpdateHandler={onGeneralFieldUpdate}
      />
      <Form
        ref={targetRef}
        name={template.formId ?? template.name}
        defaults={data.EIMSTARGET}
        fields={targetInfoTemplateSection.fields}
        layout={targetInfoTemplateSection.metadata.layout?.rows}
        fieldUpdateHandler={onTargetFieldUpdate}
        i18nKeyPrefix={i18nKeyPrefix}
        customValidationFunctionMap={customValidationFunctionMap}
      />
      {netype === 'GPRS' && (
        <React.Fragment>
          <div className="header">
            {t('warrant.target.form.ericssonNE.gprsTargetInformation')}
          </div>
          <Form
            ref={gprsTargetRef}
            name={template.formId ?? template.name}
            defaults={data.EIMSGPRSTRGT}
            fields={(gprsTargetInfoTemplateSection as FormTemplateSection).fields}
            layout={(gprsTargetInfoTemplateSection as FormTemplateSection).metadata.layout?.rows}
            fieldUpdateHandler={onGPRSTargetFieldUpdate}
            i18nKeyPrefix={i18nKeyPrefix}
          />
        </React.Fragment>
      )}
      {netype === 'MSC' &&
       (
       <React.Fragment>
         <div className="header">
           {t('warrant.target.form.ericssonNE.mscTargetInformation')}
         </div>
         <Form
           ref={mscTargetRef}
           name={template.formId ?? template.name}
           defaults={data.EIMSMSCTRGT}
           fields={(mscTargetInfoTemplateSection as FormTemplateSection).fields}
           layout={(mscTargetInfoTemplateSection as FormTemplateSection).metadata.layout?.rows}
           fieldUpdateHandler={onMSCTargetFieldUpdate}
           i18nKeyPrefix={i18nKeyPrefix}
         />
       </React.Fragment>
       )}
      {netype === 'FIX' && (
      <React.Fragment>
        <div className="header">
          {t('warrant.target.form.ericssonNE.fixTargetInformation')}
        </div>
        <Form
          ref={fixTargetRef}
          name={template.formId ?? template.name}
          defaults={data.EIMSWRLNTRGT}
          fields={(fixTargetInfoTemplateSection as FormTemplateSection).fields}
          layout={(fixTargetInfoTemplateSection as FormTemplateSection).metadata.layout?.rows}
          fieldUpdateHandler={onFIXTargetFieldUpdate}
          i18nKeyPrefix={i18nKeyPrefix}
        />
      </React.Fragment>
      )}
      {(netype === 'OV_SIPSERVER' || netype === 'OV_MOWLAN' || netype === 'OV_GPRS') && (
      <React.Fragment>
        <div className="header">
          {t('warrant.target.form.ericssonNE.mcTargetInformation')}
        </div>
        <Form
          ref={mcTargetRef}
          name={template.formId ?? template.name}
          defaults={data.EIMSMC}
          fields={(mcTargetInfoTemplateSection as FormTemplateSection).fields}
          layout={(mcTargetInfoTemplateSection as FormTemplateSection).metadata.layout?.rows}
          fieldUpdateHandler={onMcTargetFieldUpdate}
          i18nKeyPrefix={i18nKeyPrefix}
        />
      </React.Fragment>
      )}
      <div className={`${type === 'CD' || netype === 'HLR' || netype === 'OV_MOWLAN' || netype === 'OV_SIPSERVER' || netype === 'OV_GPRS' ? 'display-none' : ''}`}>
        <div className="header">
          {t('warrant.target.form.ericssonNE.monitoringCenter')}
        </div>
        <EricssonNETargetInfoTabs
          ref={tabsRef}
          id="monitoring"
          formTemplateSection={monitoringTargetInfoTemplateSection as FormTemplateSection}
          template={template}
          data={data}
          isEditing={isEditing}
          i18nKeyPrefix={i18nKeyPrefix}
          onFieldUpdate={onMonitoringFieldUpdate}
          onTabAdd={handleTabAdd}
          onTabRemove={handleTabRemove}
        />
      </div>
    </div>
  );
};

export default connector(EricssonNETargetInfo);
